import React, { useEffect, useState } from 'react'
import Base from '../../base'
import { CustomButton } from '../../components'
import { WemoloTypography } from '../../components/Wemolo'
import { SupportedFunctionalities, useLiveViewStateStore, useZoomFocusStore } from './LiveViewStore'
import { LiveViewUI } from './LiveViewUI'
import {
    getBrightnessRange,
    getBrightnessValue,
    getSupportedFunctionalitiesRequest,
    getZoomAndFocusRange,
    getZoomAndFocusValues
} from './api/requests'
import { LiveViewError } from './components/LiveViewError'
import { useLiveViewError } from './hooks'
import styles from './LiveViewContainer.module.css'

interface LiveViewProps {}
const getIsCurrentLiveStreamStarted = (streamStarted: Record<string, boolean>, baseURL: string) => {
    return streamStarted[baseURL]
}

const LiveView: React.FC<LiveViewProps> = () => {
    const [loading, setLoading] = useState(false)
    const setZoom = useZoomFocusStore((state) => state.setZoom)
    const setFocus = useZoomFocusStore((state) => state.setFocus)
    const setIsIdle = useLiveViewStateStore((state) => state.setIsIdle)
    const idle = useLiveViewStateStore((state) => state.idle)
    const baseURL = useLiveViewStateStore((state) => state.baseURL)
    const setRange = useLiveViewStateStore((state) => state.setRange)
    const loadingZoomFocus = useLiveViewStateStore((state) => state.loadingZoomFocus)
    const setBrightness = useLiveViewStateStore((state) => state.setBrightness)
    const selectedTab = useLiveViewStateStore((state) => state.selectedTab)
    const loadingBrightness = useLiveViewStateStore((state) => state.loadingBrightness)
    const setSupportedFunctionalities = useLiveViewStateStore((state) => state.setSupportedFunctionalities)
    const liveViewObject = useLiveViewStateStore((state) => state.liveViewObject)
    const streamStarted = useLiveViewStateStore((state) => state.streamStarted)
    const setStreamStarted = useLiveViewStateStore((state) => state.setStreamStarted)
    const { errorMessage, handleSupportedFunctionalitiesError, resetErrors } = useLiveViewError()

    const getZoomAndFocusValuesAction = async () => {
        const response = await getZoomAndFocusValues(baseURL)
        const data = response.data
        setZoom(data?.zoom)
        setFocus(data?.focus)
    }
    const getZoomAndFocusRangeAction = async (requestBaseURL: string) => {
        const response = await getZoomAndFocusRange(requestBaseURL)
        const data = response.data
        setRange('zoom', data?.zoomMin, data?.zoomMax, data?.zoomStep)
        setRange('focus', data?.focusMin, data?.focusMax, data?.focusStep)
    }
    const getBrightnessRangeAction = async (requestBaseURL: string) => {
        const response = await getBrightnessRange(requestBaseURL)
        const data = response.data
        setRange('brightness', data?.min, data?.max, data?.step)
    }
    const getSupportedFunctionalitiesZoomFocusBrightness = async (): Promise<boolean> => {
        const requestBaseURL = baseURL
        if (!requestBaseURL) {
            return false
        }

        setLoading(true)
        try {
            resetErrors()
            const response = await getSupportedFunctionalitiesRequest(requestBaseURL)
            // Read current baseURL directly from store to avoid closure capture
            let currentBaseURL = useLiveViewStateStore.getState().baseURL

            // Check if we've switched to a different device while request was in flight
            if (currentBaseURL !== requestBaseURL) {
                return false
            }

            const data = response.data.supported_functionalities
            setSupportedFunctionalities(data as SupportedFunctionalities)

            if (data?.brightness) await getBrightnessRangeAction(requestBaseURL)
            if (data?.zoom_focus) await getZoomAndFocusRangeAction(requestBaseURL)

            // Read again after all requests complete
            currentBaseURL = useLiveViewStateStore.getState().baseURL

            // Check again after all requests complete
            if (currentBaseURL !== requestBaseURL) {
                return false
            }
            return true
        } catch (error) {
            // Read current baseURL directly from store to avoid closure capture
            const currentBaseURL = useLiveViewStateStore.getState().baseURL
            // Check if we've switched to a different device
            if (currentBaseURL !== requestBaseURL) {
                return false
            }

            handleSupportedFunctionalitiesError(error)
            return false
        } finally {
            setLoading(false)
        }
    }
    const getBrightnessValueAction = async () => {
        const response = await getBrightnessValue(baseURL)
        const data = response.data
        setBrightness(data?.brightness ? Math.round(data.brightness * 100) / 100 : 0)
    }

    useEffect(() => {
        if (baseURL) {
            resetErrors()
            setStreamStarted(baseURL, false)
            setLoading(false)
        }
    }, [baseURL])

    useEffect(() => {
        let timeoutId: NodeJS.Timeout
        let isMounted = true

        const fetchData = async () => {
            if (
                !idle &&
                baseURL &&
                isMounted &&
                liveViewObject &&
                getIsCurrentLiveStreamStarted(streamStarted, baseURL)
            ) {
                try {
                    if (!loadingZoomFocus && selectedTab === 'focusZoom') {
                        await getZoomAndFocusValuesAction()
                    }
                    if (!loadingBrightness && selectedTab === 'brightness') {
                        await getBrightnessValueAction()
                    }
                } catch {
                    // A device request failed (e.g. gateway 502 when the device is unreachable).
                    // Already surfaced via createGenericRequest; swallow here so this polling loop's
                    // rejection isn't an unhandled promise rejection. The finally reschedules a retry.
                } finally {
                    if (isMounted) {
                        timeoutId = setTimeout(fetchData, 1000)
                    }
                }
            }
        }

        fetchData()

        return () => {
            isMounted = false
            clearTimeout(timeoutId)
        }
    }, [idle, loadingZoomFocus, selectedTab, baseURL, liveViewObject, streamStarted[baseURL as string]])

    if (baseURL && !getIsCurrentLiveStreamStarted(streamStarted, baseURL)) {
        return (
            <div className={styles.startStreamWrapper}>
                {errorMessage && (
                    <WemoloTypography variant="body-small" tag="div" className={styles.errorMessage}>
                        {errorMessage}
                    </WemoloTypography>
                )}
                <CustomButton
                    extraProps={{ minWidth: '100px' }}
                    disabled={loading}
                    onClick={async () => {
                        const success = await getSupportedFunctionalitiesZoomFocusBrightness()
                        if (success) {
                            setStreamStarted(baseURL as string, true)
                            setIsIdle(false)
                        }
                    }}
                >
                    {loading ? 'Loading...' : errorMessage ? 'Retry' : 'Start stream'}
                </CustomButton>
            </div>
        )
    }

    if (errorMessage) {
        return <LiveViewError message={errorMessage} />
    }

    return (
        <div>
            <LiveViewUI />
        </div>
    )
}

export const LiveViewContainer = () => {
    return (
        <Base>
            <LiveView />
        </Base>
    )
}
